"use client";

import { FC, PropsWithChildren, createContext, useContext } from "react";
import {
  CustomWatchlist,
  Environment,
  useCustomWatchlist,
} from "../hooks/useCustomWatchlist";

interface ICustomWatchlistContextProps {
  watchlistId: string;
  environment: Environment;
}

const MAX_WATCHLIST_ITEMS = 50;
interface ICustomWatchlistContext {
  deleteItem: (instrumentId: number) => void;
  data: CustomWatchlist | undefined;
  updateName: (newName: string) => void;
  addInstrument: (instrumentId: number) => void;
  updateQuantity: (instrumentId: number, quantity: number) => void;
}

const CustomWatchlistContext =
  createContext<ICustomWatchlistContext | null>(null);

const useCustomWatchlistLogic = ({
  watchlistId,
  environment,
}: ICustomWatchlistContextProps) => {
  const { data, removeInstrument, updateDisplayName, addInstrument, updateQuantity } =
    useCustomWatchlist(watchlistId, environment);

  const deleteItem = (insturmentId: number) => {
    removeInstrument(insturmentId);
  };

  const updateName = (newName: string) => {
    updateDisplayName(newName);
  };

  const onAddInstrument = (instrumentId: number) => {
    if ((data?.instruments ?? []).length >= MAX_WATCHLIST_ITEMS) {
      alert(
        `You may only have ${MAX_WATCHLIST_ITEMS} items in a watchlist. Please remove an item before adding another.`
      );
      return;
    }
    addInstrument(instrumentId);
  };

  return {
    data,
    deleteItem,
    updateName,
    addInstrument: onAddInstrument,
    updateQuantity,
  };
};

export const CustomWatchlistProvider: FC<
  PropsWithChildren<ICustomWatchlistContextProps>
> = ({ children, watchlistId, environment }) => {
  const { data, deleteItem, updateName, addInstrument, updateQuantity } =
    useCustomWatchlistLogic({
      watchlistId,
      environment,
    });

  return (
    <CustomWatchlistContext.Provider
      value={{
        data,
        deleteItem,
        updateName,
        addInstrument,
        updateQuantity,
      }}
    >
      {children}
    </CustomWatchlistContext.Provider>
  );
};

export const useCustomWatchlistContext = () => {
  const value = useContext(CustomWatchlistContext);
  if (!value) {
    throw new Error("CustomWatchlistContext is not initialized");
  }
  return value;
};
